import React, { useEffect, useState } from "react";
import "./ImagePicker.css";
import PropTypes from "prop-types";
import { Cancel } from "@mui/icons-material";
import { useMaterialUIController } from "context";
import { useDispatch } from "react-redux";
import { handleAlert } from "redux/festures/alertSlice";

const ImagePicker = ({
  images,
  setImages,
  name,
  multiple,
  isImageURLs,
  isImageURLsImages,
  accept,
  maxSizeMB,
  targetWidth,
  targetHeight,
  exactDimensions,
  ...rest
}) => {
  const dispatch = useDispatch();
  const [imageURLs, setImageURLs] = useState([]);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  useEffect(() => {
    if (images && multiple && images?.length > 0) {
      const newImageURls = [];
      images && images?.forEach((image) => newImageURls.push(URL.createObjectURL(image)));
      setImageURLs(newImageURls);
    } else setImageURLs([]);

    if (images && typeof images === "object" && !multiple) {
      const newImageURLs = [];

      if (images instanceof File || images instanceof Blob) {
        newImageURLs.push(URL.createObjectURL(images));
        setImageURLs(newImageURLs);
      } else if (Array.isArray(images)) {
        images.forEach((image) => {
          if (image instanceof File || image instanceof Blob) {
            newImageURLs.push(URL.createObjectURL(image));
          }
        });

        setImageURLs(newImageURLs);
      }
    }
  }, [images]);

  const validateDimensions = (file, reqWidth, reqHeight, isExact) => {
    return new Promise((resolve) => {
      if (!reqWidth && !reqHeight) return resolve({ valid: true });
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        if (isExact) {
          if ((reqWidth && img.width !== reqWidth) || (reqHeight && img.height !== reqHeight)) {
            resolve({
              valid: false,
              msg: `Image "${file.name}" resolution (${img.width}×${img.height} px) does not match required specs (${reqWidth}×${reqHeight} px).`,
            });
            return;
          }
        }
        resolve({ valid: true });
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve({ valid: true });
      };
      img.src = objectUrl;
    });
  };

  async function onImageChange(e) {
    if (e.target.files && e.target.files.length) {
      const files = Array.from(e.target.files);
      const validFiles = [];

      const limitMB = maxSizeMB || 50;

      for (const file of files) {
        // 1. Check file size (default 50MB max)
        if (file.size > limitMB * 1024 * 1024) {
          const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
          dispatch(
            handleAlert({
              isOpen: true,
              type: "error",
              msg: `File "${file.name}" is too large (${fileSizeMB} MB)! Maximum allowed size is ${limitMB} MB.`,
            })
          );
          e.target.value = "";
          return;
        }

        // 2. Check file format (if accept is image/* or contains image)
        if (
          file.type &&
          !file.type.startsWith("image/") &&
          (!accept || accept.includes("image"))
        ) {
          dispatch(
            handleAlert({
              isOpen: true,
              type: "error",
              msg: `File "${file.name}" is not a valid image format. Please select an image file (PNG, JPG, WEBP).`,
            })
          );
          e.target.value = "";
          return;
        }

        // 3. Check image dimensions if targetWidth / targetHeight passed
        if (targetWidth || targetHeight) {
          const dimResult = await validateDimensions(
            file,
            targetWidth,
            targetHeight,
            exactDimensions
          );
          if (!dimResult.valid) {
            dispatch(
              handleAlert({
                isOpen: true,
                type: "error",
                msg: dimResult.msg,
              })
            );
            e.target.value = "";
            return;
          }
        }

        validFiles.push(file);
      }

      if (validFiles.length > 0) {
        if (multiple) {
          setImages(validFiles);
        } else {
          setImages(validFiles[0]);
        }

        const newImageURLs = validFiles?.map((file) => URL.createObjectURL(file));
        setImageURLs(newImageURLs);
      } else {
        setImages(null);
        setImageURLs([]);
      }
    } else {
      setImages(null);
      setImageURLs([]);
    }
  }

  // console.log(imageURLs);
  return (
    <div style={{ width: "100%" }}>
      <input
        type="file"
        id={name || "file"}
        name={name}
        className="custom-file-input"
        multiple={multiple ? true : false}
        accept={accept ? accept : "image/*"}
        onChange={onImageChange}
        {...rest}
        style={{ width: "100%" }}
      />
      <div style={{ display: "flex", flexWrap: "wrap", width: "100%" }}>
        {imageURLs &&
          imageURLs.map((imageSrc, i) => {
            return (
              <div key={i} style={{ display: "flex", alignItems: "flex-start" }}>
                <span
                  style={{
                    display: "inline-block",
                    width: "50px",
                    height: "50px",
                    margin: "0 0.5rem",
                  }}
                >
                  <img
                    className="Image"
                    key={i}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    src={imageSrc}
                  />
                </span>
                <span
                  className="cross"
                  style={{
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    if (multiple) {
                      const temp = [...images];
                      temp.splice(i, 1);
                      setImages([...temp]);
                    } else setImages(null);
                    document.getElementById(name || "file").value = "";
                  }}
                >
                  <Cancel
                    sx={({ palette: { dark, white, info } }) => ({
                      color: darkMode ? white?.main : dark.main,
                    })}
                  />
                </span>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default ImagePicker;

ImagePicker.propTypes = {
  setImages: PropTypes.array.isRequired,
  name: PropTypes.string,
  value: PropTypes.string,
  className: PropTypes.string,
  multiple: PropTypes.string,
  images: PropTypes.any,
  accept: PropTypes.string,
  maxSizeMB: PropTypes.number,
  targetWidth: PropTypes.number,
  targetHeight: PropTypes.number,
  exactDimensions: PropTypes.bool,
  isImageURLs: PropTypes.any,
  isImageURLsImages: PropTypes.any,
};

// : (isImageURLs &&
//   Object.entries(isImageURLs).map(([key, value], i) => {
//     // console.log(value, "value");
//     return (
//       <div key={i} style={{ display: "flex", alignItems: "flex-start" }}>
//         <span
//           style={{
//             display: "inline-block",
//             width: "50px",
//             height: "50px",
//             margin: "0 0.5rem",
//           }}
//         >
//           <img
//             className="Image"
//             key={i}
//             style={{ width: "100%", height: "100%" }}
//             src={`${process.env.REACT_APP_URI}/${value}`}
//           />
//         </span>
//       </div>
//     );
//   })) ||
// (isImageURLsImages &&
//   Object.entries(isImageURLsImages).map(([key, value], i) => {
//     // console.log(value, "value");
//     return (
//       value &&
//       value.map((isValue, index) => (
//         <div key={index} style={{ display: "flex", alignItems: "flex-start" }}>
//           <span
//             style={{
//               display: "inline-block",
//               width: "50px",
//               height: "50px",
//               margin: "0 0.5rem",
//             }}
//           >
//             <img
//               className="Image"
//               key={i}
//               style={{ width: "100%", height: "100%" }}
//               src={`${process.env.REACT_APP_URI}/${isValue}`}
//             />
//           </span>
//         </div>
//       ))
//     );
//   }))
